import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BillingAddressNotVerifiedModal } from '@buy-viasat/account-information/src/components/BillingAddressNotVerifiedModal';
import { ConfirmBillingAddressModal } from '@buy-viasat/account-information/src/components/ConfirmBillingAddressModal';
import { ConfirmShippingAddressModal } from '@buy-viasat/account-information/src/components/ConfirmShippingAddressModal';
import { ShippingAddressNotVerifiedModal } from '@buy-viasat/account-information/src/components/ShippingAddressNotVerifiedModal';

import { BackButtonPromptModal } from '@buy-viasat/order-confirmation/src/components/BackButtonPromptModal';
import { ScheduleLaterModal } from '@buy-viasat/schedule-installation/src/components/ScheduleLaterModal';
import {
  appActions,
  Modals,
  selectCountry,
  selectCurrentModal,
  selectCustomerType,
  selectIsModalVisible,
} from '@buy-viasat/redux/src/app';
import {
  selectInlineBillingStreetAndPostalCode,
  selectInlineScrubBillingAddress,
  selectInlineShippingAddress,
  selectInlineShippingStreetAndPostalCode,
} from '@buy-viasat/redux/src/address';
import { personalInformationActions } from '@buy-viasat/redux/src/personal-information';
import { paymentInformationActions } from '@buy-viasat/redux/src/payment-information';
import {
  selectInlineScrubAddress,
  selectInlineServiceStreetAndPostalCode,
  selectScrubLocation,
  serviceabilityActions,
} from '@buy-viasat/redux/src/serviceability';
import { ConfirmServiceAddressModal } from '@buy-viasat/serviceability/src/components/ConfirmServiceAddressModal';
import { AddressNotVerifiedModal } from '@buy-viasat/serviceability/src/components/AddressNotVerifiedModal';
import { NoAddressFoundModal } from '@buy-viasat/serviceability/src/components/NoAddressFoundModal';
import { NoAvailablePlansModal } from '@buy-viasat/serviceability/src/components/NoAvailablePlansModal';
import { UpdateBillingAddressModal } from '@buy-viasat/payment-information/src/components/UpdateBillingAddressModal';
import { PaymentErrorModal } from '@buy-viasat/payment-information/src/components/PaymentErrorModal';
import { CAEModal } from '@buy-viasat/credit-verification/src/components/CAEModal';
import { GeneralErrorModal } from '../GeneralErrorModal';
import { RemoveOptionalServiceModal } from '@buy-viasat/addons/src/components/RemoveOptionalServiceModal';
import { navActions, Routes, selectCurrentAppRoute } from '@buy-viasat/redux/src/navigator';
import { cartActions } from '@buy-viasat/redux/src/cart';
import { ClosingOfferRetryErrorModal } from '../ClosingOfferRetryErrorModal';
import { ClosingOfferNotEligibleModal } from '../ClosingOfferNotEligibleModal';
import { planActions, selectSelectedPlan } from '@buy-viasat/redux/src/plan';
import { closingOffersActions } from '@buy-viasat/redux/src/closingOffers';

export const BeamModalSelector = () => {
  const isModalVisible = useSelector(selectIsModalVisible);
  const currentModal = useSelector(selectCurrentModal);
  const scrubAddress = useSelector(selectScrubLocation);
  const country = useSelector(selectCountry);
  const inlineScrubAddress = useSelector(selectInlineScrubAddress);
  const inlineBillingScrubAddress = useSelector(selectInlineScrubBillingAddress);
  const inlineShippingScrubAddress = useSelector(selectInlineShippingAddress);
  const serviceAddressStreetAndZip = useSelector(selectInlineServiceStreetAndPostalCode);
  const shippingAddressStreetAndZip = useSelector(selectInlineShippingStreetAndPostalCode);
  const billingAddressStreetAndZip = useSelector(selectInlineBillingStreetAndPostalCode);
  const currentRoute = useSelector(selectCurrentAppRoute);
  const customerType = useSelector(selectCustomerType);
  const dispatch = useDispatch();

  const selectedPlan = useSelector(selectSelectedPlan);

  const { id, name, price, offerId, isCafII, characteristics, extensionTypes } = selectedPlan || {};
  const { isRegulated, dataCap, productFamily, inflectionPointText } = characteristics || {};
  const handleUpsertWithClosingOfferRetryErrorModalPrimaryAction = () => {
    dispatch(closingOffersActions.setUpsertWithClosingOffersError(false));
    dispatch(appActions.setIsModalVisible(false));
  };
  const handleUpsertWithClosingOfferRetryErrorModalSecondaryAction = () => {
    dispatch(closingOffersActions.setUpsertWithClosingOffersError(false));
    dispatch(closingOffersActions.setSkipClosingOffer(true));

    dispatch(
      planActions.planOnNext({
        isRegulated: isRegulated === 'true',
        id: id || '',
        name: name || '',
        planPrice: price || 0,
        offerId: offerId || '',
        planPromo: price || 0,
        isCafII: isCafII || false,
        dataCap: dataCap || '',
        extensionProducts: extensionTypes as string[],
        productFamily: productFamily || '',
        inflectionPointText: inflectionPointText || '',
        customerType,
        country,
      }),
    );
    dispatch(appActions.setIsModalVisible(false));
    dispatch(closingOffersActions.setSkipClosingOffer(false));
  };

  const handleGenericErrorModalSecondaryAction = () => {
    if (currentRoute === Routes.PERSONAL_INFORMATION) {
      dispatch(navActions.routeUserTo(Routes.PLAN));
    }
    dispatch(appActions.setIsModalVisible(false));
  };
  const handleGenericErrorModalPrimaryAction = () => {
    dispatch(appActions.onModalConfirm(Modals.ERROR));
    dispatch(appActions.setIsModalVisible(false));
  };

  const handleClosingOfferNotEligiblePrimaryAction = () => {
    dispatch(closingOffersActions.setUpsertWithClosingOffersError(false));
    dispatch(appActions.setIsModalVisible(false));
    dispatch(closingOffersActions.setSkipClosingOffer(false));
  };
  const handleClosingOfferNotEligibleSecondaryAction = () => {
    dispatch(closingOffersActions.setUpsertWithClosingOffersError(false));
    dispatch(appActions.setIsModalVisible(false));
    dispatch(navActions.routeUserTo(Routes.PLAN));
  };

  const handleModalAddressNotFound = (modal: Modals | string) => {
    dispatch(personalInformationActions.setIsPersonalInformationLoading(false));

    if (modal) {
      dispatch(appActions.setModalVisible(modal as Modals));
    } else {
      dispatch(appActions.setIsModalVisible(false));
    }
  };

  React.useEffect(() => {
    if (!isModalVisible) return;
    const listener = (event: KeyboardEvent): void => {
      if (event.code !== 'Enter') return;
      dispatch(appActions.onModalConfirm(currentModal));
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [dispatch, isModalVisible, currentModal]);

  if (!isModalVisible) {
    return null;
  } else {
    switch (currentModal) {
      //Confirm Address Modal #2
      case Modals.ADDRESS_NOT_VERIFIED:
        return (
          <AddressNotVerifiedModal
            primaryAction={() => dispatch(serviceabilityActions.useInlineServiceAddress())}
            secondaryAction={() => dispatch(appActions.setIsModalVisible(false))}
            suggestedAddressText={inlineScrubAddress}
          />
        );
      case Modals.CONFIRM_SERVICE_ADDRESS:
        if (country !== scrubAddress.address.countryCode) {
          return (
            //No Address Found Modal
            <NoAddressFoundModal
              primaryAction={() => dispatch(appActions.onModalCancel(Modals.NO_ADDRESS_FOUND))}
              secondaryAction={() => dispatch(appActions.onModalCancel(Modals.DEFAULT))}
            />
          );
        }
        return (
          //Confirm Address Modal #1
          <ConfirmServiceAddressModal
            radioModalAddresses={{
              inlineScrubAddress: inlineScrubAddress,
              serviceAddress: serviceAddressStreetAndZip,
            }}
            primaryAction={{
              scrubbedAddressRadioButton: () => dispatch(appActions.onModalConfirm(Modals.CONFIRM_SERVICE_ADDRESS)),
              serviceAddressRadioButton: () => dispatch(appActions.onModalCancel(Modals.CONFIRM_SERVICE_ADDRESS)),
            }}
            secondaryAction={() => {
              dispatch(serviceabilityActions.resetServiceAddress());
              dispatch(appActions.setIsModalVisible(false));
            }}
          />
        );
      case Modals.NO_ADDRESS_FOUND:
        return (
          <NoAddressFoundModal
            primaryAction={() => dispatch(appActions.onModalConfirm(Modals.NO_ADDRESS_FOUND))}
            secondaryAction={() => dispatch(appActions.onModalCancel(Modals.DEFAULT))}
          />
        );
      case Modals.NO_AVAILABLE_PLANS:
      case Modals.NO_SERVICE:
        return (
          <NoAvailablePlansModal
            primaryAction={() => dispatch(appActions.onModalConfirm(Modals.NO_AVAILABLE_PLANS))}
            secondaryAction={() => dispatch(appActions.onModalCancel(Modals.DEFAULT))}
          />
        );
      case Modals.ERROR:
        return (
          <GeneralErrorModal
            primaryAction={handleGenericErrorModalPrimaryAction}
            secondaryAction={handleGenericErrorModalSecondaryAction}
          />
        );
      case Modals.UPSERT_WITH_CLOSING_OFFER_RETRY_ERROR:
        return (
          <ClosingOfferRetryErrorModal
            primaryAction={handleUpsertWithClosingOfferRetryErrorModalPrimaryAction}
            secondaryAction={handleUpsertWithClosingOfferRetryErrorModalSecondaryAction}
          />
        );
      case Modals.CLOSING_OFFER_NOT_ELIGIBLE:
        return (
          <ClosingOfferNotEligibleModal
            primaryAction={handleClosingOfferNotEligiblePrimaryAction}
            secondaryAction={handleClosingOfferNotEligibleSecondaryAction}
          />
        );
      case Modals.BILLING_ADDRESS_NOT_VERIFIED:
        return (
          <BillingAddressNotVerifiedModal
            suggestedAddressText={inlineBillingScrubAddress}
            primaryAction={() => handleModalAddressNotFound(Modals.NO_ADDRESS_FOUND)}
            secondaryAction={() => handleModalAddressNotFound('')}
          />
        );
      case Modals.SHIPPING_ADDRESS_NOT_VERIFIED:
        return (
          <ShippingAddressNotVerifiedModal
            suggestedAddressText={inlineShippingScrubAddress}
            primaryAction={() => handleModalAddressNotFound(Modals.NO_ADDRESS_FOUND)}
            secondaryAction={() => handleModalAddressNotFound('')}
          />
        );
      case Modals.CONFIRM_BILLING_ADDRESS:
        return (
          <ConfirmBillingAddressModal
            radioModalAddresses={{
              inlineScrubAddress: inlineBillingScrubAddress,
              billingAddress: billingAddressStreetAndZip,
            }}
            primaryAction={{
              scrubbedAddressRadioButton: () => dispatch(appActions.onModalConfirm(Modals.CONFIRM_BILLING_ADDRESS)),
              billingAddressStreetAndZip: () => dispatch(appActions.onModalCancel(Modals.CONFIRM_BILLING_ADDRESS)),
            }}
            secondaryAction={() => {
              dispatch(appActions.setIsModalVisible(false));
            }}
          />
        );
      case Modals.CONFIRM_SHIPPING_ADDRESS:
        return (
          <ConfirmShippingAddressModal
            radioModalAddresses={{
              inlineScrubAddress: inlineShippingScrubAddress,
              shippingAddress: shippingAddressStreetAndZip,
            }}
            primaryAction={{
              scrubbedAddressRadioButton: () => dispatch(appActions.onModalConfirm(Modals.CONFIRM_SHIPPING_ADDRESS)),
              shippingAddressStreetAndZip: () => dispatch(appActions.onModalCancel(Modals.CONFIRM_SHIPPING_ADDRESS)),
            }}
            secondaryAction={() => {
              dispatch(appActions.setIsModalVisible(false));
            }}
          />
        );
      case Modals.UPDATE_BILLING_ADDRESS:
        return (
          <UpdateBillingAddressModal
            primaryAction={() => dispatch(paymentInformationActions.onEditBillingAddress())}
            secondaryAction={() => dispatch(appActions.setIsModalVisible(false))}
          />
        );
      case Modals.PAYMENT_INFORMATION_ERROR:
        return (
          <PaymentErrorModal
            primaryAction={() => {
              dispatch(appActions.setIsModalVisible(false));
            }}
          />
        );
      case Modals.CAE:
        return <CAEModal primaryAction={() => dispatch(appActions.onModalCancel(Modals.CAE))} />;
      case Modals.BACK_BUTTON_PROMPT:
        return (
          <BackButtonPromptModal
            primaryAction={() => {
              dispatch(appActions.onModalCancel(Modals.BACK_BUTTON_PROMPT));
              dispatch(navActions.routeUserTo(Routes.SERVICEABILITY));
            }}
            secondaryAction={() => dispatch(appActions.onModalCancel(Modals.BACK_BUTTON_PROMPT))}
          />
        );

      case Modals.SCHEDULE_LATER:
        return (
          <ScheduleLaterModal
            primaryAction={() => {
              dispatch(appActions.setIsModalVisible(false));
              dispatch(navActions.next());
            }}
            secondaryAction={() => dispatch(appActions.setIsModalVisible(false))}
          />
        );
      case Modals.REMOVE_OPTIONAL_SERVICE:
        return (
          <RemoveOptionalServiceModal
            primaryAction={() => {
              dispatch(appActions.setIsModalVisible(false));
              dispatch(cartActions.setOptionalServicesEditMode(false));
            }}
            secondaryAction={(optionalService) => {
              dispatch(appActions.setIsModalVisible(false));
              dispatch(cartActions.removeCartItems(optionalService));
              dispatch(cartActions.setOptionalServicesEditMode(false));
            }}
          />
        );
      default:
        return null;
    }
  }
};
